'use client'

import React, { FC, useCallback, useMemo, useState } from 'react'
import Invoice from '@/components/search-input/components/invoice'
import Wallet from '@/utils/wallet'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { NETWORK, API_URL } from '@/utils/constants'
import numberWithCommas from '@/utils/format-number'
import axios from 'axios'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'
import { isSnipeProtectionOffered } from '@/utils/market/secure-purchase'
import { useSnipeProtection } from '@/utils/market/use-snipe-protection'
import { snipeProtectionInvoiceItem } from '@/components/snipe-protection-toggle'
import {
	assertStandardListingAllowed,
	describeSecureListingResult,
	isolateTargetForFailures,
	listSecureItems
} from '@/utils/market/secure-listing-client'
import {
	availabilityForCollection,
	planForProtectedFailure,
	protectedErrorCode,
	protectionAssetFromRow,
	resolveListingPlan,
	SNIPE_TEMPORARILY_UNAVAILABLE
} from '@/utils/market/protection-support'
import { toastListingSuccess } from '@/utils/market/listing-success-toast'

export interface SelectedListingInfo {
	blockHeight: number
	inscription: any // Full inscription data with id
	priceBTC: number // Price in BTC
}

interface BitmapListingInvoiceProps {
	selected: SelectedListingInfo[]
	onComplete: () => void
	onClose: () => void
}

const BitmapListingInvoice: FC<BitmapListingInvoiceProps> = ({ selected, onComplete, onClose }) => {
	const [invoiceError, setInvoiceError] = useState<string | null>(null)
	const [isSubmitting, setIsSubmitting] = useState(false)

	const { exchangeRate } = useExchangeRate()

	// Calculate total price user will receive
	const totalPriceSats = useMemo(() => {
		return selected.reduce((total, item) => {
			return total + item.priceBTC * 100_000_000 // Convert BTC to sats
		}, 0)
	}, [selected])

	const totalPriceBTC = totalPriceSats / 1e8
	const totalPriceUSD = totalPriceBTC * exchangeRate

	const snipeProtection = useSnipeProtection('ordinal', 'bitmap')
	const [shapePrompt, setShapePrompt] = React.useState<string | null>(null)
	const [shapeAck, setShapeAck] = React.useState<
		'ambiguous_inscription_assets' | 'postage_too_small' | null
	>(null)

	const handleSubmit = useCallback(async (sellerConfirmed = false) => {
		setIsSubmitting(true)
		setInvoiceError(null)

		try {
			const prices = selected.map((item) => Math.round(item.priceBTC * 100_000_000))
			let availability = snipeProtection.apiStatus
			let caps = snipeProtection.caps
			if (availability === 'loading' || availability === 'probe_failed') {
				const fresh = await isSnipeProtectionOffered('ordinal')
				availability = fresh.status
				caps = fresh.caps
			}
			availability = availabilityForCollection(availability, 'bitmap')
			const assets = selected.map((item) => {
				const asset = protectionAssetFromRow(
					{ ...item.inscription, collection: item.inscription?.collection || { slug: 'bitmap' } },
					'bitmap'
				)
				if (shapeAck === 'ambiguous_inscription_assets') return { ...asset, sharedUtxoCount: 2 }
				if (shapeAck === 'postage_too_small') return { ...asset, postageSats: 1 }
				return asset
			})
			const plans = assets.map((asset) =>
				resolveListingPlan({
					asset,
					availability,
					caps,
					shapeConfirmed: sellerConfirmed
				})
			)
			const standardIndexes = plans.flatMap((plan, index) => (plan.action === 'standard' ? [index] : []))
			const protectIndexes = plans.flatMap((plan, index) => (plan.action === 'protect' ? [index] : []))
			if (plans.some((plan) => plan.action === 'confirm') && !sellerConfirmed) {
				const message = plans.find((plan) => plan.action === 'confirm')
				const text = message && message.action === 'confirm' ? message.message : ''
				setShapePrompt(text)
				setInvoiceError(text)
				return
			}
			if (
				plans.some((plan) => plan.action === 'block') &&
				protectIndexes.length === 0 &&
				standardIndexes.length === 0
			) {
				const message = plans.find((plan) => plan.action === 'block')
				setInvoiceError(
					message && message.action === 'block' ? message.message : SNIPE_TEMPORARILY_UNAVAILABLE
				)
				return
			}
			if (protectIndexes.length > 0) {
				try {
					const result = await listSecureItems(
						protectIndexes.map((index) => {
							const item = selected[index]
							return {
								inscriptionId: item.inscription.id,
								priceSats: prices[index],
								protocol: 'ordinal' as const,
								inscription: {
									...item.inscription,
									collection: item.inscription?.collection || { slug: 'bitmap' }
								}
							}
						})
					)
					const shapeFailure = result.failures.find(
						(failure) =>
							failure.code === 'ambiguous_inscription_assets' || failure.code === 'postage_too_small'
					)
					if (shapeFailure) {
						setShapeAck(shapeFailure.code as 'ambiguous_inscription_assets' | 'postage_too_small')
						const next = planForProtectedFailure(shapeFailure.code, { caps })
						const text = next.action === 'confirm' ? next.message : shapeFailure.message
						setShapePrompt(text)
						if (result.listed > 0) {
							toastListingSuccess({
								title: 'Partially listed',
								description: `You listed ${result.listed} with snipe protection.`,
								slug: 'bitmap',
								count: result.listed
							})
						}
						setInvoiceError(text)
						return
					}
					if (result.failures.length > 0) {
						const code = result.failures[0].code
						const next = planForProtectedFailure(code, {
							shapeConfirmed: sellerConfirmed,
							caps,
							provenProtocol: result.failures[0].protocol
						})
						if (result.listed > 0) {
							toastListingSuccess({
								title: 'Partially listed',
								description: `You listed ${result.listed} with snipe protection.`,
								slug: 'bitmap',
								count: result.listed
							})
						}
						setInvoiceError(
							(next.action === 'block' && next.message) ||
								(next.action === 'standard' && next.note) ||
								(next.action === 'confirm' && next.message) ||
								result.failures[0].message ||
								SNIPE_TEMPORARILY_UNAVAILABLE
						)
						return
					}
					const summary = describeSecureListingResult(
						result,
						result.listed === 1 ? 'bitmap' : 'bitmaps'
					)
					toastListingSuccess({
						title: summary.title,
						description: summary.description,
						...(summary.failed ? { variant: 'destructive' as const } : {}),
						isolate: isolateTargetForFailures(
							result.failures,
							protectIndexes.map((index) => ({
								inscriptionId: selected[index].inscription.id,
								outpoint: selected[index].inscription.outpoint
							})),
							'bitmap'
						),
						slug: 'bitmap',
						count: result.listed
					})
					if (summary.failed || standardIndexes.length === 0) {
						if (!summary.failed) onComplete()
						return
					}
				} catch (e) {
					const code = protectedErrorCode(e)
					const next = code
						? planForProtectedFailure(code, { shapeConfirmed: sellerConfirmed, caps })
						: { action: 'block' as const, message: '' }
					if (
						next.action === 'confirm' &&
						(code === 'ambiguous_inscription_assets' || code === 'postage_too_small')
					) {
						setShapeAck(code)
						setShapePrompt(next.message)
						setInvoiceError(next.message)
						return
					}
					setInvoiceError(
						(next.action === 'block' && next.message) ||
							(next.action === 'standard' && next.note) ||
							(next.action === 'confirm' && next.message) ||
							(e as { message?: string })?.message ||
							SNIPE_TEMPORARILY_UNAVAILABLE
					)
					return
				}
			}

			if (standardIndexes.length === 0) return
			const standardAssets = standardIndexes.map((index) => assets[index])
			assertStandardListingAllowed(standardAssets, caps)
			const inscriptionIds = standardIndexes.map((index) => selected[index].inscription.id)
			const standardPrices = standardIndexes.map((index) => prices[index])
			const psbt = await Wallet.getEscrows(inscriptionIds, standardPrices, {})
			let totalCreatedEscrows = 0
			let totalSkippedInputs = 0
			let skippedInputs: any[] = []

			// Handle array response (multiple PSBTs) or single PSBT
			if (Array.isArray(psbt)) {
				for (const p of psbt) {
					const { rawtx, success } = await Wallet.signEscrow(p)
					if (success) {
						const { data } = await axios.post(`${API_URL}/market/escrow-bulk`, { psbt: rawtx })
						const summary = Wallet.assertEscrowResponse(data)
						totalCreatedEscrows += summary.createdEscrows
						totalSkippedInputs += summary.skippedInputsCount
						skippedInputs = skippedInputs.concat(summary.skippedInputs || [])
					}
				}
			} else {
				const { rawtx, success } = await Wallet.signEscrow(psbt)
				if (success) {
					const { data } = await axios.post(`${API_URL}/market/escrow-bulk`, { psbt: rawtx })
					const summary = Wallet.assertEscrowResponse(data)
					totalCreatedEscrows += summary.createdEscrows
					totalSkippedInputs += summary.skippedInputsCount
					skippedInputs = skippedInputs.concat(summary.skippedInputs || [])
				}
			}

			if (totalCreatedEscrows <= 0) {
				throw new Error('No escrows were created')
			}

			if (totalSkippedInputs > 0) {
				const skippedSummary = Wallet.formatEscrowSkips(skippedInputs)
				toastListingSuccess({
					title: 'Partially Listed',
					description: `Created ${totalCreatedEscrows} escrows. Skipped ${totalSkippedInputs} inputs${
						skippedSummary ? `: ${skippedSummary}` : '.'
					}.`,
					slug: 'bitmap',
					count: totalCreatedEscrows
				})
			} else {
				toastListingSuccess({
					title: 'Success!',
					description: `You listed ${inscriptionIds.length} bitmap${
						inscriptionIds.length !== 1 ? 's' : ''
					} for sale`,
					slug: 'bitmap',
					count: inscriptionIds.length
				})
			}

			onComplete()
		} catch (e: any) {
			console.error('Error listing bitmaps:', e)
			setInvoiceError(e?.response?.data?.message || e?.message || 'An error occurred while listing')
		} finally {
			setIsSubmitting(false)
		}
	}, [
		selected,
		onComplete,
		snipeProtection.apiStatus,
		snipeProtection.caps,
		shapeAck
	])

	const protectionPreview = useMemo(() => {
		if (snipeProtection.status === 'loading' || selected.length === 0) {
			return { shield: false, note: shapePrompt, confirmingShape: !!shapePrompt }
		}
		let shield = false
		let note: string | null = shapePrompt
		let shape = 0
		let other = 0
		for (const item of selected) {
			let asset = protectionAssetFromRow(
				{ ...item.inscription, collection: item.inscription?.collection || { slug: 'bitmap' } },
				'bitmap'
			)
			if (shapeAck === 'ambiguous_inscription_assets') asset = { ...asset, sharedUtxoCount: 2 }
			if (shapeAck === 'postage_too_small') asset = { ...asset, postageSats: 1 }
			const plan = resolveListingPlan({
				asset,
				availability: snipeProtection.status,
				caps: snipeProtection.caps,
				shapeConfirmed: false
			})
			if (plan.action === 'protect') {
				shield = true
				other += 1
			} else if (plan.action === 'confirm') {
				shape += 1
				note = note || plan.message
			} else if (plan.action === 'standard') {
				if (plan.shape) shape += 1
				else other += 1
				note = note || plan.note
			} else {
				other += 1
				note = note || plan.message
			}
		}
		return { shield, note, confirmingShape: !!shapePrompt || (shape > 0 && other === 0) }
	}, [selected, shapePrompt, shapeAck, snipeProtection.status, snipeProtection.caps])

	const invoiceItems = useMemo(
		() => [
			{
				title: 'You Receive',
				value: `${totalPriceBTC.toFixed(8)} ${NETWORK} / $${numberWithCommas(
					totalPriceUSD.toFixed(2)
				)}`
			},
			...snipeProtectionInvoiceItem(
				protectionPreview.shield && !shapePrompt,
				protectionPreview.note
			)
		],
		[totalPriceBTC, totalPriceUSD, protectionPreview, shapePrompt]
	)

	if (selected.length === 0) {
		return null
	}

	return (
		<div className="fixed inset-0 z-50 pointer-events-none">
			{/* Backdrop - click to close */}
			<div
				className="absolute inset-0 pointer-events-auto"
				onClick={onClose}
				role="button"
				tabIndex={0}
				onKeyDown={onKeyboardClick(onClose)}
			/>
			{/* Invoice - sits on top of footer, full width on mobile, right-aligned on desktop */}
			<div className="absolute left-0 right-0 md:left-auto md:right-0 bottom-[88px] md:w-[380px] pointer-events-auto border-t border-border md:border md:rounded-t-lg md:mr-4 shadow-2xl overflow-hidden">
				<Invoice
					open={true}
					onSubmit={() => handleSubmit(protectionPreview.confirmingShape)}
					action={
						protectionPreview.confirmingShape
							? 'Confirm list without snipe protection'
							: `List ${selected.length} Inscription${selected.length !== 1 ? 's' : ''}`
					}
					items={invoiceItems}
					errorMessage={invoiceError as any}
					disabled={isSubmitting}
				/>
			</div>
		</div>
	)
}

export default BitmapListingInvoice
